import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import API_BASE_URL from '../../config/api';
import './CarForm.css';

const CarForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(id);
  const currentYear = new Date().getFullYear();
  
  const isAddImagesMode = location.state?.addImagesOnly || false;

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');

  const [formData, setFormData] = useState({
    marca: '', model: '', pret: '', anFabricatie: currentYear,
    kilometraj: '', carburant: 'Diesel', transmisie: 'Manuală', tractiune: 'Față',
    caroserie: 'Sedan', capacitateCilindrica: '', putere: '', culoare: '',
    disponibilitate: 'Avem pe stoc', locatie: 'București', tvaDeductibil: false,
    garantieInclusa: false, descriere: '', dotari: '', numarInmatriculare: '',
    nrProprietari: 1, serieSasiu: ''
  });

  // 🆕 UNIFIED IMAGE ARRAY - toate imaginile într-un singur array
  // Fiecare imagine: { id: unique, type: 'existing'|'new', src: string, file?: File, path?: string }
  const [allImages, setAllImages] = useState([]);
  
  // Drag state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const MAX_IMAGES_PER_UPLOAD = 30;
  const MAX_TOTAL_IMAGES = 50;

  const transmisieOptions = ['Automată', 'Manuală', 'Semi-automată'];
  const disponibilitateOptions = ['Avem pe stoc', 'Nu este pe stoc'];
  const carburantOptions = ['Diesel', 'Benzină', 'Hibrid', 'Benzină + GPL', 'GPL', 'Hybrid Plug-in', 'Electric'];
  const tractiuneOptions = ['Față', 'Spate', '4x4', 'AWD', 'FWD', 'RWD'];
  const caroserieOptions = ['Sedan', 'SUV', 'Hatchback', 'Break', 'Coupe', 'Cabrio', 'Monovolum', 'Pick-up', 'Microbus'];

  useEffect(() => {
    if (isEdit) fetchCarData();
  }, [id]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      allImages.forEach(img => {
        if (img.type === 'new' && img.src.startsWith('blob:')) {
          URL.revokeObjectURL(img.src);
        }
      });
    };
  }, []);

  const fetchCarData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/cars/${id}`);
      if (!response.ok) throw new Error('Mașina nu a fost găsită');
      const data = await response.json();
      
      if (data.success) {
        const car = data.data;
        
        setFormData({ 
          ...car, 
          dotari: car.dotari ? car.dotari.join('\n') : '',
          tvaDeductibil: car.tvaDeductibil || false,
          garantieInclusa: car.garantieInclusa || false,
          nrProprietari: car.nrProprietari || 1
        });
        
        // Convert existing images to unified format
        if (car.imagini && car.imagini.length > 0) {
          const existingImages = car.imagini.map((path, index) => ({
            id: `existing-${index}-${Date.now()}`,
            type: 'existing',
            src: `${API_BASE_URL}${path}`,
            path: path // Keep original path for backend
          }));
          setAllImages(existingImages);
          console.log('📷 Loaded images:', existingImages.length);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
    return new Promise((resolve) => {
      if (file.size < 500 * 1024) { resolve(file); return; }
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
          canvas.width = width; canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > MAX_IMAGES_PER_UPLOAD) {
      alert(`Poți adăuga maxim ${MAX_IMAGES_PER_UPLOAD} imagini odată.`);
      return;
    }
    
    if (allImages.length + files.length > MAX_TOTAL_IMAGES) {
      alert(`Limita este ${MAX_TOTAL_IMAGES}. Poți adăuga maxim ${MAX_TOTAL_IMAGES - allImages.length}.`);
      return;
    }

    setUploadStatus('Se procesează imaginile...');
    
    const compressedFiles = await Promise.all(files.map(file => compressImage(file)));
    
    // Add new images to unified array
    const newImages = compressedFiles.map((file, index) => ({
      id: `new-${Date.now()}-${index}`,
      type: 'new',
      src: URL.createObjectURL(file),
      file: file
    }));
    
    setAllImages(prev => [...prev, ...newImages]);
    setUploadStatus('');
    e.target.value = '';
  };

  const removeImage = (indexToRemove) => {
    setAllImages(prev => {
      const imageToRemove = prev[indexToRemove];
      // Cleanup blob URL if it's a new image
      if (imageToRemove.type === 'new' && imageToRemove.src.startsWith('blob:')) {
        URL.revokeObjectURL(imageToRemove.src);
      }
      return prev.filter((_, i) => i !== indexToRemove);
    });
  };

  // 🆕 DRAG & DROP HANDLERS
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Make drag image semi-transparent
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== draggedIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    setAllImages(prev => {
      const newArray = [...prev];
      const [draggedItem] = newArray.splice(draggedIndex, 1);
      newArray.splice(dropIndex, 0, draggedItem);
      return newArray;
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // 🆕 TOUCH DRAG SUPPORT
  const [touchDragIndex, setTouchDragIndex] = useState(null);
  
  const handleTouchStart = (index) => {
    setTouchDragIndex(index);
  };

  const handleTouchEnd = () => {
    setTouchDragIndex(null);
  };

  const moveImage = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setAllImages(prev => {
      const newArray = [...prev];
      const [item] = newArray.splice(fromIndex, 1);
      newArray.splice(toIndex, 0, item);
      return newArray;
    });
  };

  // Move buttons for mobile
  const moveImageUp = (index) => {
    if (index > 0) moveImage(index, index - 1);
  };

  const moveImageDown = (index) => {
    if (index < allImages.length - 1) moveImage(index, index + 1);
  };

  const moveImageToFirst = (index) => {
    if (index > 0) moveImage(index, 0);
  };

  const uploadWithProgress = (url, formData, method = 'POST') => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          setUploadProgress(Math.round((e.loaded / e.total) * 100));
          setUploadStatus(`Se încarcă: ${Math.round((e.loaded / e.total) * 100)}%`);
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { reject(new Error('Răspuns invalid')); }
        } else {
          try { reject(new Error(JSON.parse(xhr.responseText).message)); }
          catch { reject(new Error(`Eroare ${xhr.status}`)); }
        }
      };
      xhr.onerror = () => reject(new Error('Eroare conexiune'));
      xhr.ontimeout = () => reject(new Error('Timeout'));
      xhr.open(method, url);
      xhr.timeout = 180000;
      const token = localStorage.getItem('adminToken');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setUploadProgress(0);

    try {
      const formDataToSend = new FormData();

      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (key === 'dotari') {
          const dotariArray = formData[key].split('\n').map(i => i.trim()).filter(i => i);
          dotariArray.forEach(d => formDataToSend.append('dotari[]', d));
        } else if (!['imagini', 'imagineaPrincipala', '_id', '__v', 'createdAt', 'updatedAt', 'slug'].includes(key)) {
          formDataToSend.append(key, formData[key]);
        }
      });

      // 🆕 Process images IN ORDER
      // We need to send: orderedExistingPaths[] and newFiles with their positions
      const imageOrder = [];
      const newFiles = [];
      
      allImages.forEach((img, index) => {
        if (img.type === 'existing') {
          // For existing images, send the path with position
          imageOrder.push({ type: 'existing', path: img.path, position: index });
        } else {
          // For new images, track which file goes where
          imageOrder.push({ type: 'new', fileIndex: newFiles.length, position: index });
          newFiles.push(img.file);
        }
      });

      // Send image order as JSON
      formDataToSend.append('imageOrder', JSON.stringify(imageOrder));
      
      // Send new files
      newFiles.forEach(file => formDataToSend.append('imagini', file));

      console.log('📤 Sending image order:', imageOrder);
      console.log('📤 New files count:', newFiles.length);

      const url = isEdit ? `${API_BASE_URL}/api/cars/${id}` : `${API_BASE_URL}/api/cars`;
      const data = await uploadWithProgress(url, formDataToSend, isEdit ? 'PUT' : 'POST');

      if (data.success) {
        console.log('✅ Save successful! Total images:', data.data.imagini?.length);
        alert(isEdit ? 'Mașina actualizată!' : 'Mașina adăugată!');
        navigate('/admin/dashboard');
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      console.error('❌ Submit error:', err);
      setError(err.message);
      alert('Eroare: ' + err.message);
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const generateYears = () => Array.from({length: currentYear - 1984}, (_, i) => currentYear - i);
  
  const getTotalSize = () => {
    const newFilesSize = allImages
      .filter(img => img.type === 'new' && img.file)
      .reduce((s, img) => s + img.file.size, 0);
    return (newFilesSize / 1048576).toFixed(2);
  };

  const newImagesCount = allImages.filter(img => img.type === 'new').length;
  const existingImagesCount = allImages.filter(img => img.type === 'existing').length;

  if (loading) return <div className="form-loading"><div className="spinner"></div><p>Se încarcă...</p></div>;

  // Simplified mode for adding images only
  if (isAddImagesMode) {
    return (
      <div className="car-form-container">
        <div className="form-header">
          <button onClick={() => navigate(-1)} className="back-link">← Înapoi</button>
          <h1>Editează imagini: {formData.marca} {formData.model}</h1>
        </div>
        <form onSubmit={handleSubmit} className="car-form">
          <div className="form-section">
            <h2>Imagini ({allImages.length}/{MAX_TOTAL_IMAGES})</h2>
            <p className="drag-hint">💡 Trage imaginile pentru a le reordona. Prima imagine va fi imaginea principală.</p>
            
            <div className="upload-area">
              <label htmlFor="images" className="upload-box">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.5">
                  <path d="M12 5v14M5 12h14"/><rect x="3" y="3" width="18" height="18" rx="3"/>
                </svg>
                <span style={{color:'#2563eb',fontWeight:600}}>Adaugă imagini</span>
                <small>Max {MAX_IMAGES_PER_UPLOAD} odată</small>
              </label>
              <input type="file" id="images" accept="image/*" multiple onChange={handleImageChange} hidden disabled={allImages.length >= MAX_TOTAL_IMAGES} />
            </div>

            {newImagesCount > 0 && (
              <p style={{color:'#16a34a',margin:'10px 0'}}>
                ✓ {newImagesCount} imagini noi ({getTotalSize()} MB)
              </p>
            )}

            {/* 🆕 UNIFIED DRAGGABLE IMAGE GRID */}
            {allImages.length > 0 && (
              <div className="image-preview-grid draggable-grid">
                {allImages.map((img, index) => (
                  <div
                    key={img.id}
                    className={`image-preview-item draggable-item ${
                      draggedIndex === index ? 'dragging' : ''
                    } ${dragOverIndex === index ? 'drag-over' : ''} ${
                      img.type === 'new' ? 'new-image' : ''
                    }`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    <img 
                      src={img.src} 
                      alt={`Imagine ${index + 1}`}
                      onError={(e) => {e.target.src = 'https://via.placeholder.com/150x100?text=Error'}}
                    />
                    
                    {/* Position number */}
                    <span className="image-position">{index + 1}</span>
                    
                    {/* Badges */}
                    {/* {index === 0 && <span className="primary-badge">Principal</span>}
                    {img.type === 'new' && <span className="new-badge">NOU</span>} */}
                    
                    {/* Remove button */}
                    <button 
                      type="button" 
                      className="remove-image-btn" 
                      onClick={() => removeImage(index)}
                    >×</button>
                    
                    {/* Mobile reorder buttons */}
                    <div className="reorder-buttons">
                      {index > 0 && (
                        <button type="button" onClick={() => moveImageUp(index)} title="Mută sus">↑</button>
                      )}
                      {index > 0 && (
                        <button type="button" onClick={() => moveImageToFirst(index)} title="Fă principal">★</button>
                      )}
                      {index < allImages.length - 1 && (
                        <button type="button" onClick={() => moveImageDown(index)} title="Mută jos">↓</button>
                      )}
                    </div>
                    
                    {/* Drag handle */}
                    <div className="drag-handle">⋮⋮</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {submitting && (
            <div className="upload-progress-section">
              <div className="progress-bar-container">
                <div className="progress-bar" style={{width: `${uploadProgress}%`}}/>
              </div>
              <p>{uploadStatus}</p>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={() => navigate(-1)}>Anulează</button>
            <button type="submit" className="btn-submit" disabled={submitting}>
              {submitting ? uploadStatus : `Salvează (${allImages.length} imagini)`}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Full form
  return (
    <div className="car-form-container">
      <div className="form-header">
        <button onClick={() => navigate(-1)} className="back-link">← Înapoi</button>
        <h1>{isEdit ? 'Editează mașina' : 'Adaugă mașină'}</h1>
      </div>
      {error && <div className="form-error">{error}</div>}
      <form onSubmit={handleSubmit} className="car-form">
        <div className="form-section">
          <h2 className="section-title">Informații de bază</h2>
          <div className="form-grid">
            <div className="form-group"><label>Marca *</label><input name="marca" value={formData.marca} onChange={handleInputChange} required /></div>
            <div className="form-group"><label>Model *</label><input name="model" value={formData.model} onChange={handleInputChange} required /></div>
            <div className="form-group"><label>Preț EUR *</label><input type="number" name="pret" value={formData.pret} onChange={handleInputChange} required /></div>
            <div className="form-group"><label>An *</label><select name="anFabricatie" value={formData.anFabricatie} onChange={handleInputChange}>{generateYears().map(y=><option key={y}>{y}</option>)}</select></div>
            <div className="form-group"><label>Km *</label><input type="number" name="kilometraj" value={formData.kilometraj} onChange={handleInputChange} required /></div>
            <div className="form-group"><label>Culoare</label><input name="culoare" value={formData.culoare} onChange={handleInputChange} /></div>
            <div className="form-group"><label>Nr. locuri</label><input name="numarInmatriculare" value={formData.numarInmatriculare} onChange={handleInputChange} /></div>
            <div className="form-group"><label>Serie șasiu</label><input name="serieSasiu" value={formData.serieSasiu} onChange={handleInputChange} /></div>
            <div className="form-group"><label>Proprietari</label><input type="number" name="nrProprietari" value={formData.nrProprietari} onChange={handleInputChange} /></div>
            <div className="form-group"><label>Locație</label><input name="locatie" value={formData.locatie} onChange={handleInputChange} /></div>
          </div>
        </div>

        <div className="form-section">
          <h2 className="section-title">Specificații</h2>
          <div className="form-grid">
            <div className="form-group"><label>Carburant *</label><select name="carburant" value={formData.carburant} onChange={handleInputChange}>{carburantOptions.map(o=><option key={o}>{o}</option>)}</select></div>
            <div className="form-group"><label>Transmisie *</label><select name="transmisie" value={formData.transmisie} onChange={handleInputChange}>{transmisieOptions.map(o=><option key={o}>{o}</option>)}</select></div>
            <div className="form-group"><label>Tracțiune *</label><select name="tractiune" value={formData.tractiune} onChange={handleInputChange}>{tractiuneOptions.map(o=><option key={o}>{o}</option>)}</select></div>
            <div className="form-group"><label>Caroserie *</label><select name="caroserie" value={formData.caroserie} onChange={handleInputChange}>{caroserieOptions.map(o=><option key={o}>{o}</option>)}</select></div>
            <div className="form-group"><label>CC *</label><input type="number" name="capacitateCilindrica" value={formData.capacitateCilindrica} onChange={handleInputChange} required /></div>
            <div className="form-group"><label>CP *</label><input type="number" name="putere" value={formData.putere} onChange={handleInputChange} required /></div>
          </div>
        </div>

        <div className="form-section">
          <h2 className="section-title">Status</h2>
          <div className="form-grid">
            <div className="form-group"><label>Disponibilitate</label><select name="disponibilitate" value={formData.disponibilitate} onChange={handleInputChange}>{disponibilitateOptions.map(o=><option key={o}>{o}</option>)}</select></div>
            <div className="form-group checkbox-group"><label><input type="checkbox" name="tvaDeductibil" checked={formData.tvaDeductibil} onChange={handleInputChange}/> TVA Deductibil</label></div>
            <div className="form-group checkbox-group"><label><input type="checkbox" name="garantieInclusa" checked={formData.garantieInclusa} onChange={handleInputChange}/> Garanție</label></div>
          </div>
        </div>

        <div className="form-section">
          <h2 className="section-title">Descriere</h2>
          <div className="form-group full-width"><textarea name="descriere" value={formData.descriere} onChange={handleInputChange} rows="5" placeholder="Descrierea mașinii..."/></div>
          <div className="form-group full-width"><label>Dotări (una pe linie)</label><textarea name="dotari" value={formData.dotari} onChange={handleInputChange} rows="6" placeholder="Climatronic&#10;Navigație&#10;Scaune încălzite&#10;..."/></div>
        </div>
        
        {/* 🆕 IMAGES SECTION WITH DRAG & DROP */}
        <div className="form-section">
          <h2 className="section-title">Imagini ({allImages.length}/{MAX_TOTAL_IMAGES})</h2>
          <p className="drag-hint">💡 Trage imaginile pentru a le reordona. Prima imagine va fi imaginea principală.</p>
          
          <div className="upload-area">
            <label htmlFor="images" className="upload-box">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.5">
                <path d="M12 5v14M5 12h14"/><rect x="3" y="3" width="18" height="18" rx="3"/>
              </svg>
              <span style={{color:'#2563eb',fontWeight:600}}>Adaugă imagini</span>
              <small>Max {MAX_IMAGES_PER_UPLOAD} odată</small>
            </label>
            <input type="file" id="images" accept="image/*" multiple onChange={handleImageChange} hidden disabled={allImages.length >= MAX_TOTAL_IMAGES}/>
          </div>

          {newImagesCount > 0 && (
            <p style={{color:'#16a34a',margin:'10px 0'}}>
              ✓ {newImagesCount} imagini noi ({getTotalSize()} MB) | 
              {existingImagesCount > 0 && ` ${existingImagesCount} existente`}
            </p>
          )}

          {/* 🆕 UNIFIED DRAGGABLE IMAGE GRID */}
          {allImages.length > 0 && (
            <div className="image-preview-grid draggable-grid">
              {allImages.map((img, index) => (
                <div
                  key={img.id}
                  className={`image-preview-item draggable-item ${
                    draggedIndex === index ? 'dragging' : ''
                  } ${dragOverIndex === index ? 'drag-over' : ''} ${
                    img.type === 'new' ? 'new-image' : ''
                  }`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  <img 
                    src={img.src} 
                    alt={`Imagine ${index + 1}`}
                    onError={(e) => {e.target.src = 'https://via.placeholder.com/150x100?text=Error'}}
                  />
                  
                  {/* Position number */}
                  <span className="image-position">{index + 1}</span>
                  
                  {/* Badges */}
                  {index === 0 && <span className="primary-badge">Principal</span>}
                  {img.type === 'new' && <span className="new-badge">NOU</span>}
                  
                  {/* Remove button */}
                  <button 
                    type="button" 
                    className="remove-image-btn" 
                    onClick={() => removeImage(index)}
                  >×</button>
                  
                  {/* Mobile reorder buttons */}
                  <div className="reorder-buttons">
                    {index > 0 && (
                      <button type="button" onClick={() => moveImageUp(index)} title="Mută sus">↑</button>
                    )}
                    {index > 0 && (
                      <button type="button" onClick={() => moveImageToFirst(index)} title="Fă principal">★</button>
                    )}
                    {index < allImages.length - 1 && (
                      <button type="button" onClick={() => moveImageDown(index)} title="Mută jos">↓</button>
                    )}
                  </div>
                  
                  {/* Drag handle */}
                  <div className="drag-handle">⋮⋮</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {submitting && (
          <div className="upload-progress-section">
            <div className="progress-bar-container">
              <div className="progress-bar" style={{width:`${uploadProgress}%`}}/>
            </div>
            <p>{uploadStatus}</p>
          </div>
        )}
        
        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={() => navigate(-1)}>Anulează</button>
          <button type="submit" className="btn-submit" disabled={submitting}>
            {submitting ? uploadStatus : (isEdit ? 'Actualizează mașina' : 'Adaugă mașina')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CarForm;